import { useState, useEffect, useRef } from 'react';

type TeamKind = 'black' | 'white';

interface PledgeModalProps {
  show: boolean;
  team: TeamKind | null;
  onConfirm: () => void;
}

// ─── Burst particles on checkbox ───────────────────────────────────────────

const BURST_COLORS = ['#d4a843', '#f5d080', '#ffd700', '#ffe066', '#ffffff', '#f0c040', '#e8b84b'];

interface BurstParticle {
  id: number;
  dx: string;
  dy: string;
  size: number;
  color: string;
  delay: string;
  duration: string;
}

interface BurstEvent {
  key: string;
  x: number;
  y: number;
  particles: BurstParticle[];
}

function createBurst(cx: number, cy: number): BurstEvent {
  const count = 18;
  return {
    key: `${Date.now()}-${Math.random()}`,
    x: cx,
    y: cy,
    particles: Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
      const dist = 28 + Math.random() * 64;
      return {
        id: i,
        dx: `${Math.cos(angle) * dist}px`,
        dy: `${Math.sin(angle) * dist}px`,
        size: 3 + Math.random() * 5,
        color: BURST_COLORS[i % BURST_COLORS.length],
        delay: `${Math.random() * 0.08}s`,
        duration: `${0.55 + Math.random() * 0.25}s`,
      };
    }),
  };
}

// ─── Pledge items ───────────────────────────────────────────────────────────

const PLEDGES = [
  { icon: 'ri-heart-fill',       text: '我會全心投入活動，不管多累多無聊都會提供滿滿情緒價值' },
  { icon: 'ri-team-fill',        text: '我會把團隊榮譽放在首位，在團隊需要我的時候盡一份心力' },
  { icon: 'ri-shield-star-fill', text: '安全第一，開心第二，美送愛共！' },
];

// ─── Main component ─────────────────────────────────────────────────────────

export default function PledgeModal({ show, team, onConfirm }: PledgeModalProps) {
  const [visible, setVisible]     = useState(false);
  const [checked, setChecked]     = useState<boolean[]>([false, false, false]);
  const [confirming, setConfirming] = useState(false);
  const [shaking, setShaking]     = useState(false);
  const [bursts, setBursts]       = useState<BurstEvent[]>([]);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 60);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
      setChecked([false, false, false]);
      setConfirming(false);
      setBursts([]);
    }
  }, [show]);

  if (!show && !confirming) return null;

  const allChecked = checked.every(Boolean);

  const handleToggle = (i: number) => {
    const wasChecked = checked[i];
    setChecked(prev => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
    // Burst only when checking (not unchecking)
    if (!wasChecked) {
      const btn = btnRefs.current[i];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const burst = createBurst(rect.left + rect.width * 0.12, rect.top + rect.height / 2);
        setBursts(prev => [...prev, burst]);
        setTimeout(() => setBursts(prev => prev.filter(b => b.key !== burst.key)), 900);
      }
    }
  };

  const handleConfirm = () => {
    if (!allChecked) {
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      return;
    }
    setConfirming(true);
    // Dispatch celebration event so HomeContent can show it above MemberProfileModal
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('srs-join-celebration', { detail: { team } }));
      onConfirm();
    }, 350);
  };

  return (
    <>
      {/* ── Burst particles ── */}
      {bursts.map(burst => (
        <div
          key={burst.key}
          className="fixed pointer-events-none"
          style={{ left: burst.x, top: burst.y, zIndex: 65 }}
        >
          {burst.particles.map(p => (
            <div
              key={p.id}
              className="absolute rounded-full"
              style={{
                width: `${p.size}px`,
                height: `${p.size}px`,
                background: p.color,
                boxShadow: `0 0 ${p.size * 2}px ${p.color}88`,
                transform: 'translate(-50%, -50%)',
                '--dx': p.dx,
                '--dy': p.dy,
                animation: `pledgeBurstFly ${p.duration} ${p.delay} ease-out both`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      ))}

      {/* ── Main modal ── */}
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center px-4"
        style={{
          opacity: confirming ? 0 : 1,
          transition: confirming ? 'opacity 0.3s ease-out' : 'none',
          pointerEvents: confirming ? 'none' : 'auto',
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 transition-opacity duration-500"
          style={{
            background: 'rgba(0,0,0,0.88)',
            backdropFilter: 'blur(6px)',
            opacity: visible ? 1 : 0,
          }}
        />

        {/* Card */}
        <div
          className="relative w-full max-w-sm rounded-2xl overflow-hidden transition-all duration-500"
          style={{
            background: 'linear-gradient(160deg, #110e00 0%, #0d0d0d 60%)',
            border: '1px solid rgba(212,168,67,0.3)',
            boxShadow: '0 0 60px rgba(212,168,67,0.15)',
            transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.96)',
            opacity: visible ? 1 : 0,
          }}
        >
          {/* Top glow */}
          <div
            className="absolute top-0 left-0 right-0 h-24 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 50% -10%, rgba(212,168,67,0.22) 0%, transparent 70%)',
            }}
          />

          <div className="relative px-6 pt-8 pb-6">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, #d4a843)' }} />
                <span className="text-[#d4a843] text-[8px] tracking-[0.35em] uppercase font-black">Activity Pledge</span>
                <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, #d4a843, transparent)' }} />
              </div>
              <h2
                className="text-white font-black text-2xl mb-1.5"
                style={{ fontFamily: 'Georgia, serif', textShadow: '0 0 20px rgba(212,168,67,0.3)' }}
              >
                活動宣示
              </h2>
              <p className="text-[#555] text-xs tracking-wide">勾選每一條，代表你認同並承諾遵守</p>
            </div>

            {/* Pledge items */}
            <div className="space-y-3 mb-6">
              {PLEDGES.map((pledge, i) => (
                <button
                  key={i}
                  ref={el => { btnRefs.current[i] = el; }}
                  type="button"
                  onClick={() => handleToggle(i)}
                  className="w-full text-left flex items-start gap-3 rounded-xl p-3.5 transition-all duration-300 cursor-pointer"
                  style={{
                    background: checked[i]
                      ? 'linear-gradient(135deg, rgba(212,168,67,0.14) 0%, rgba(212,168,67,0.04) 100%)'
                      : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${checked[i] ? 'rgba(212,168,67,0.45)' : 'rgba(255,255,255,0.07)'}`,
                    transform: checked[i] ? 'scale(1.01)' : 'scale(1)',
                  }}
                >
                  {/* Checkbox */}
                  <div
                    className="shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200 mt-0.5"
                    style={{
                      background: checked[i] ? '#d4a843' : 'transparent',
                      border: `1.5px solid ${checked[i] ? '#d4a843' : '#444'}`,
                      boxShadow: checked[i] ? '0 0 8px rgba(212,168,67,0.5)' : 'none',
                    }}
                  >
                    {checked[i] && <i className="ri-check-line text-[11px] text-[#0d0d0d] font-black" />}
                  </div>

                  {/* Icon + Text */}
                  <div className="flex items-start gap-2 flex-1">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i
                        className={`${pledge.icon} text-sm transition-all duration-300`}
                        style={{ color: checked[i] ? '#d4a843' : '#555' }}
                      />
                    </div>
                    <p
                      className="text-xs leading-relaxed transition-all duration-300"
                      style={{ color: checked[i] ? '#d4a843' : '#666' }}
                    >
                      {pledge.text}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Shake hint */}
            <p
              className={`text-center text-[10px] mb-3 ${shaking ? 'pledge-shake' : ''}`}
              style={{ color: shaking ? '#e05c5c' : 'transparent', minHeight: '16px' }}
            >
              請勾選所有條目才能繼續
            </p>

            {/* Confirm button */}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full py-3.5 rounded-xl font-black text-sm tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{
                background: allChecked
                  ? 'linear-gradient(135deg, #d4a843 0%, #b8922e 100%)'
                  : 'rgba(255,255,255,0.05)',
                color: allChecked ? '#0d0d0d' : '#444',
                border: allChecked ? 'none' : '1px solid #2a2a2a',
                boxShadow: allChecked ? '0 0 20px rgba(212,168,67,0.3)' : 'none',
                opacity: confirming ? 0.6 : 1,
              }}
            >
              我已閱讀並承諾遵守 → 進入遊戲
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pledge-shake-kf {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-5px); }
          40% { transform: translateX(5px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .pledge-shake { animation: pledge-shake-kf 0.45s ease; }
        @keyframes pledgeBurstFly {
          from { transform: translate(-50%,-50%) scale(1.4); opacity: 1; }
          70%  { opacity: 0.8; }
          to   { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.1); opacity: 0; }
        }
      `}</style>
    </>
  );
}
